"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Scale, Stethoscope, Building2, GraduationCap, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const sectors = [{
  name: "Legal",
  description: "Practice-area visibility that upholds professional standards and ethical advertising rules.",
  icon: Scale
}, {
  name: "Healthcare",
  description: "Strict adherence to medical content accuracy, clinical trust, and user safety criteria.",
  icon: Stethoscope
}, {
  name: "Finance",
  description: "Clear information architecture for regulated services, compliance, and user protection.",
  icon: Building2
}, {
  name: "Education",
  description: "Structured pathways for prospective students, faculties, and academic programmes.",
  icon: GraduationCap
}];
export default function IndustryHub() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const containerStagger = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.15
      }
    }
  };
  return <section id="industryhubhero" data-nav="light" className="relative bg-background text-foreground pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden border-b border-border">
      {/* Subtle background ambient glow using blush pink secondary tint */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-1/4 -z-10 h-96 w-96 rounded-full bg-secondary/60 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -left-20 -z-10 h-72 w-72 rounded-full bg-muted/70 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial="hidden" animate="show" variants={containerStagger} className="flex flex-col items-center text-center space-y-6">
          {/* Eyebrow badge */}
          <motion.div variants={fadeIn}>
            <Badge variant="outline" className="inline-flex items-center gap-1.5 px-3 py-1 text-xs md:text-sm font-medium border-border bg-card text-foreground rounded-full shadow-2xs">
              <ShieldCheck className="size-3.5 text-accent" />
              <span>Direct freelance SEO advisory with Yassine Essaadi</span>
            </Badge>
          </motion.div>

          {/* Main Title & Subtitle */}
          <motion.div variants={fadeIn} className="max-w-4xl space-y-4">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-[1.15]">
              SEO strategy for sectors where trust and compliance come first
            </h1>
            <p className="mx-auto max-w-2xl text-base sm:text-lg md:text-xl text-muted-foreground font-sans leading-relaxed">
              I advise legal, healthcare, financial, and education firms on sustainable organic search growth. You work directly with me, ensuring every recommendation respects regulatory standards and your professional reputation.
            </p>
          </motion.div>

          {/* Action CTAs */}
          <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-sm transition-[color,background-color,border-color,box-shadow]">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium border-border hover:bg-secondary/40 transition-[color,background-color,border-color,box-shadow]">
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </motion.div>

          {/* Direct consultant commitment note */}
          <motion.div variants={fadeIn} className="flex items-center justify-center pt-4 text-xs sm:text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" />
              <span>Direct consultant access with rigorous adherence to compliance, trust, and quality guidelines</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Industry Focus Grid */}
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerStagger} className="mt-16 sm:mt-20 pt-10 border-t border-border">
          <div className="text-center mb-8">
            <h2 className="font-serif text-lg sm:text-xl font-medium text-foreground">
              Tailored search frameworks for high-scrutiny industries
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {sectors.map((sector, index) => {
            const IconComponent = sector.icon;
            return <motion.div key={sector.name} data-index={index} variants={fadeIn} className="rounded-xl border border-border bg-card p-6 shadow-2xs hover:shadow-sm transition-[color,background-color,border-color,box-shadow] hover:border-primary/30 flex flex-col justify-between text-left">
                  <div>
                    <div className="inline-flex p-2.5 rounded-lg bg-secondary text-primary mb-4">
                      <IconComponent className="size-5" />
                    </div>
                    <h3 className="font-serif text-base sm:text-lg font-semibold text-card-foreground mb-2">
                      {sector.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {sector.description}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/60">
                    <span className="text-xs font-medium text-primary inline-flex items-center gap-1">
                      Direct strategic oversight
                    </span>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}